<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';
import { computed, ref } from 'vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  x: {
    type: String
  },
  y: {
    type: String
  },
  // Caps the popup so tall content scrolls inside it instead of stretching the page.
  maxHeight: {
    type: String,
    default: 'calc(100vh - 16px)'
  },
  nestedPopups: {
    type: Boolean
  }
});

const target = ref(null);

// Bound inline rather than through `v-bind()` in the style block: Vue applies
// v-bind CSS vars to teleported content by stamping `data-v-owner` during the
// Teleport patch, and the element mounted by a <transition> misses that pass —
// so the popup would render unpositioned on the render that opens it.
const popupStyle = computed(() => ({
  left: props.x,
  top: props.y,
  maxHeight: props.maxHeight
}));

const emit = defineEmits(['outsidePopupClick']);

const toggle = (event: PointerEvent) => {
  if (target.value && props.nestedPopups) {
    const targetBounds = (target.value as HTMLElement).getBoundingClientRect();

    const elementBounds = (event.target as HTMLElement).getBoundingClientRect();

    const targetXEnd = targetBounds.x + targetBounds.width;
    const targetXStart = targetBounds.x ;

    const elementX = event.clientX;

    const elementXStart = elementBounds.x;
    const elementXEnd = elementBounds.x + elementBounds.width;

    if (elementXStart > targetXStart && elementXStart < targetXEnd) {
      if (!(elementX > elementXStart && elementX < elementXEnd)) {
        emit('outsidePopupClick');
      }
    } else {
      emit('outsidePopupClick');
    }
  } else {
    emit('outsidePopupClick');
  }
};

onClickOutside(target, toggle);

defineExpose({
  target
});
</script>

<template>
    <teleport to="body">
      <transition name="popup">
        <div class="popup"
          ref="target"
          v-if="visible"
          :style="popupStyle">
            <slot name="popupContent"></slot>
        </div>
      </transition>
    </teleport>
</template>

<style scoped>
.popup {
  padding: 0;
  margin: 0;
  position: absolute;
  background-color: white;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 8px;
  box-shadow: 0px 5px 5px -3px hsla(0, 0%, 0%, 0.2),
    0px 8px 10px 1px hsla(0, 0%, 0%, 0.14),
    0px 3px 14px 2px hsla(0, 0%, 0%, 0.12);
  z-index: 10;
  transform-origin: top left;

  /* The popup is the scroll container: tall content scrolls here rather than
     growing the document, and `overflow` also clips hover rows to the corners.
     `left`, `top` and `max-height` come from the :style binding. */
  overflow-y: auto;
  overflow-x: hidden;
  /* Stops the wheel from carrying on into the page once the list hits an end. */
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: hsla(0, 0%, 0%, 0.28) transparent;
}

.popup::-webkit-scrollbar {
  width: 8px;
}
.popup::-webkit-scrollbar-track {
  background: transparent;
}
.popup::-webkit-scrollbar-thumb {
  background-color: hsla(0, 0%, 0%, 0.24);
  border: 2px solid transparent;
  border-radius: 8px;
  background-clip: content-box;
}
.popup::-webkit-scrollbar-thumb:hover {
  background-color: hsla(0, 0%, 0%, 0.4);
}

/* Short grow-and-fade so the popup reads as coming out of its trigger. */
.popup-enter-active {
  transition: opacity 0.12s ease-out, transform 0.12s ease-out;
}
.popup-leave-active {
  transition: opacity 0.1s ease-in, transform 0.1s ease-in;
}
.popup-enter-from,
.popup-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .popup-enter-active,
  .popup-leave-active {
    transition: none;
  }
}
</style>
